import { faCog } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { getFormValues } from "redux-form";

import { analyticsStoreEvent } from "@/mk2/actions";
import {
    POST_CREATE_EDIT_FORM_NAME,
    PostFormData,
} from "@/mk2/apps/forum/components/PostCreateEditForm";
import PostCreate from "@/mk2/apps/forum/containers/PostCreate/PostCreate";
import {
    postCreateFetchTrigger,
    postCreatePendingActionTrigger,
    postCreateSetAnonymousMode,
    postCreateSetWantsMkToFoundDiscussion,
    postCreateTrigger,
} from "@/mk2/apps/forum/containers/PostCreate/PostCreate.actions";
import postCreateReducer from "@/mk2/apps/forum/containers/PostCreate/PostCreate.reducers";
import postCreateSaga from "@/mk2/apps/forum/containers/PostCreate/PostCreate.sagas";
import { getForumPostCreateState } from "@/mk2/apps/forum/containers/PostCreate/PostCreate.selectors";
import { TopicEntity, TopicSchema } from "@/mk2/apps/forum/schemas";
import { forumPostNewInCategoryUrl } from "@/mk2/apps/forum/urls";
import { Btn, BtnLayout, BtnType } from "@/mk2/components/Btn";
import { SubmitButton } from "@/mk2/components/forms/SubmitButton";
import { FormPageType } from "@/mk2/constants/enums";
import {
    mkReduxFormMapDispatchToProps,
    mkReduxFormMapStateToProps,
} from "@/mk2/decorators/mkReduxForm";
import { cachedInitialValues } from "@/mk2/helpers/form";
import { mkReduxFormCancelPage, mkReduxFormOpenPage } from "@/mk2/helpers/form.actions";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import FormPage, { FormPageDispatchProps, FormPageStateProps } from "@/mk2/pages/FormPage";
import { PageRouteProps } from "@/mk2/pages/Page";
import {
    AppState,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { UserEntity } from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import {
    FORUM_POST_CREATE_OPTIONS,
    SEND,
    SEO_TITLE_FORUM_THREAD_NEW,
} from "@/mk/autogenerated/translation";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";

const fasCogLg: FontAwesomeIconProps = { icon: faCog, size: "lg" };

interface OwnProps {}

interface StateProps extends FormPageStateProps {
    anonymousMode: boolean;
    wantsMkToFoundDiscussion: boolean;
    loadingState: LoadingState;
    requestUser: UserEntity;
    isMobile: boolean;
    topic: TopicEntity;
    initialValues: PostFormData;
    requestUserPermissions: string[];
}

interface DispatchProps extends FormPageDispatchProps {
    onLoad(topicSlug: string);
    onSave(topicSlug: string, anonymousMode: boolean, values: PostFormData);
    onPendingActionCreate(topicSlug: string, postFd: PostFormData);
    onSetAnonymousMode(topicSlug: string, enabled: boolean);
    onSetWantsMkToFoundDiscussion(topicSlug: string, enabled: boolean);
    onAnonymousMode();
    onBackToMain();
    onStoreEvent(name: string, props: any);
}

type RouteProps = PageRouteProps<{
    categorySlug: string;
    topicSlug: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class PostCreatePage extends FormPage<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const createState = getForumPostCreateState(state);
            const topic = getDenormalizedEntity<TopicEntity>(
                state,
                TopicSchema,
                createState.topicId,
            );
            const formValues = getFormValues(POST_CREATE_EDIT_FORM_NAME)(state) as PostFormData;

            return {
                ...mkReduxFormMapStateToProps(POST_CREATE_EDIT_FORM_NAME)(state),
                anonymousMode: formValues && formValues.anonymousMode === "enabled",
                wantsMkToFoundDiscussion: createState.wantsMkToFoundDiscussion,
                loadingState: createState.loadingState,
                requestUserPermissions: getRequestPermissions(state),
                isMobile: getRequestDeviceMobile(state),
                requestUser: getRequestUser(state),
                topic,
                initialValues: cachedInitialValues(
                    POST_CREATE_EDIT_FORM_NAME,
                    topic && {
                        topic,
                        title: "",
                        perex: "",
                        photos: [],
                        sticker: null,
                        anonymousMode: "disabled",
                    },
                    topic,
                ),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...mkReduxFormMapDispatchToProps(POST_CREATE_EDIT_FORM_NAME),
            onLoad: postCreateFetchTrigger,
            onSave: postCreateTrigger,
            onPendingActionCreate: postCreatePendingActionTrigger,
            onSetAnonymousMode: postCreateSetAnonymousMode,
            onSetWantsMkToFoundDiscussion: postCreateSetWantsMkToFoundDiscussion,
            onAnonymousMode: () =>
                mkReduxFormOpenPage(
                    POST_CREATE_EDIT_FORM_NAME,
                    FormPageType.AnonymousModeExplanation,
                ),
            onBackToMain: () => mkReduxFormCancelPage(POST_CREATE_EDIT_FORM_NAME),
            onStoreEvent: analyticsStoreEvent,
        };
    }

    public getPermissionRequired(_props: Props): boolean {
        return true;
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_FORUM_THREAD_NEW;
    }

    public render(props: Props): false | JSX.Element {
        return <PostCreate {...props} />;
    }

    public getDefaultBackPath(props: Props): string {
        const {
            routeParams: { categorySlug },
        } = props;
        return url(forumPostNewInCategoryUrl, { categorySlug });
    }

    public getHeaderLeft(props: Props): JSX.Element {
        const { currentFormPage, onBackToMain } = props;
        return currentFormPage === FormPageType.AnonymousModeExplanation ? (
            <Btn icon="mkicon-arrow-back@3x.png" layout={BtnLayout.Icon} onClick={onBackToMain} />
        ) : (
            super.getHeaderLeft(props)
        );
    }

    public getSubmitButton(props: Props): React.ReactElement {
        const { anonymousMode, requestUser } = props;
        return (
            <SubmitButton
                btnType={
                    isAuthenticated(requestUser) && anonymousMode ? BtnType.Black : BtnType.Blue
                }
                formName={POST_CREATE_EDIT_FORM_NAME}
                label={SEND}
            />
        );
    }

    public getHeaderAdditionalButtons(props: Props): React.ReactElement {
        const { onAnonymousMode, requestUser } = props;
        if (!isAuthenticated(requestUser)) {
            return null;
        }
        return (
            <Btn
                icon={fasCogLg}
                label={FORUM_POST_CREATE_OPTIONS}
                layout={BtnLayout.LabelIcon}
                onClick={onAnonymousMode}
                type={BtnType.BlueOutline}
            />
        );
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.forum.forumPostCreate", postCreateReducer);
        store.injectSaga("containers.forum.forumPostCreate", postCreateSaga);
    }
}
